import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wallet, Check, X, Plus, UploadCloud, Loader2 } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TableTools, useTableSearch } from "@/components/TableTools";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const barPct = (out, lim) => Math.min(100, (Number(out || 0) / (Number(lim) || 1)) * 100);
const barColor = (p) => (p > 90 ? "bg-rose-500" : p >= 70 ? "bg-amber-500" : "bg-emerald-500");
const MODES = { cash: { label: "Cash", field: "note", ref: "Note" }, usdt: { label: "USDT", field: "usdt_tx_hash", ref: "USDT Transaction Hash" }, bank: { label: "Bank", field: "bank_ref", ref: "Bank Ref / UTR" } };
const modeLabel = (s) => (MODES[s.payment_mode]?.label || (s.bank_ref ? "Bank" : "—"));
const refOf = (s) => s.usdt_tx_hash || s.bank_ref || (s.payment_mode === "cash" ? s.note : null) || "—";
const Proof = ({ s }) => (s.screenshot ? <a href={fileUrl(s.screenshot)} target="_blank" rel="noreferrer" data-testid={`settlement-proof-${s.id}`} className="text-teal-700 text-xs underline">View</a> : "—");

export function SettlementPanel({ isSuper }) {
  return isSuper ? <SuperSettlement /> : <BranchSettlement />;
}

function Stat({ label, value, testid }) {
  return <div><div className="text-xs text-slate-500">{label}</div><div data-testid={testid} className="text-lg font-bold text-slate-900">{value}</div></div>;
}

function SettlementHistory({ rows, showBranch }) {
  const badge = (s) => s === "Approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s === "Rejected" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200";
  const isCb = (s) => s.type === "chargeback";
  const hRow = (s) => [...(showBranch ? [s.branch_name || ""] : []), isCb(s) ? "Charge Back" : "Settlement", isCb(s) ? "" : modeLabel(s), s.amount ?? "",
    isCb(s) ? (s.tx_id || "") : refOf(s), s.status || "", s.reason || "", s.requested_at || ""];
  const [q, setQ, shown] = useTableSearch(rows, hRow);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 overflow-x-auto" data-testid="settlement-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold text-slate-900">Settlement history</h3>
        <TableTools id="settlement-history" query={q} onQuery={setQ} cols={[...(showBranch ? ["Branch"] : []), "Type", "Mode", "Amount", "Ref / Txn", "Status", "Reason", "Requested"]} rows={shown.map(hRow)} title="ABCD Pay — Settlement history" filename="abcdpay-settlements" />
      </div>
      {shown.length === 0 ? <p className="text-sm text-slate-500 mt-2">No settlements yet.</p> : (
        <table className="w-full mt-3 text-sm"><thead><tr className="border-b border-slate-200 text-left text-slate-500">{showBranch && <th className="py-2">Branch</th>}<th className="py-2">Type</th><th>Mode</th><th>Amount</th><th>Ref / Txn</th><th>Proof</th><th>Status</th><th>Reason</th><th>Requested</th></tr></thead>
        <tbody>{shown.map((s) => (
          <tr key={s.id} className="border-b border-slate-100" data-testid={`settlement-history-row-${s.id}`}>
            {showBranch && <td className="py-2">{s.branch_name}</td>}
            <td className="py-2">{isCb(s)
              ? <span data-testid={`history-type-chargeback-${s.id}`} className="text-xs px-2 py-0.5 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200">Charge Back</span>
              : <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">Settlement</span>}</td>
            <td data-testid={`history-mode-${s.id}`}>{isCb(s) ? "—" : modeLabel(s)}</td>
            <td>{inr(s.amount)}</td><td className="font-mono text-xs text-slate-500 max-w-[200px] truncate">{isCb(s) ? (s.tx_id || "—") : refOf(s)}</td><td><Proof s={s} /></td>
            <td><span className={`text-xs px-2 py-0.5 rounded-full border ${badge(s.status)}`}>{s.status}</span></td>
            <td className="text-slate-500">{s.reason || "—"}</td><td className="text-slate-500">{(s.requested_at || "").slice(0, 10)}</td>
          </tr>))}</tbody></table>
      )}
    </div>
  );
}

function BranchSettlement() {
  const [sum, setSum] = useState(null);
  const [rows, setRows] = useState([]);
  const empty = { amount: "", payment_mode: "bank", bank_ref: "", usdt_tx_hash: "", note: "", screenshot: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const mode = MODES[form.payment_mode];
  const onImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/branch/upload-image", fd);
      setForm((p) => ({ ...p, screenshot: data.url }));
      toast.success("Screenshot uploaded");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };
  const load = () => {
    api.get("/branch/settlement").then((r) => setSum(r.data)).catch(() => {});
    api.get("/settlements").then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!String(form[mode.field] || "").trim()) return toast.error(`${mode.ref} is required`);
    if (!form.screenshot) return toast.error("Screenshot is required");
    const settleable = sum ? Math.round((sum.outstanding - sum.pending) * 100) / 100 : 0;
    if (amt > settleable) return toast.error(`Amount can't exceed ${inr(settleable)} (outstanding minus pending)`);
    setBusy(true);
    try {
      await api.post("/settlements", {
        amount: amt, payment_mode: form.payment_mode, screenshot: form.screenshot,
        bank_ref: form.payment_mode === "bank" ? form.bank_ref.trim() : null,
        usdt_tx_hash: form.payment_mode === "usdt" ? form.usdt_tx_hash.trim() : null,
        note: form.note.trim() || null,
      });
      toast.success("Settlement request submitted");
      setForm(empty);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const pct = sum ? barPct(sum.outstanding, sum.max_limit) : 0;
  return (
    <div className="space-y-6" data-testid="settlement-panel-branch">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
        <div className="flex items-center gap-2"><Wallet className="w-5 h-5 text-teal-600" /><h2 className="text-lg font-bold text-slate-900">Payin Limit & Settlement</h2></div>
        {sum?.limit_reached && <div data-testid="limit-reached-banner" className="mt-3 text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2">Limit reached — your QRs are temporarily not accepting new payments. Settle your outstanding to free up available limit.</div>}
        <div className="mt-4 h-3 rounded-full bg-slate-100 overflow-hidden"><div data-testid="settlement-progress" className={`h-full ${barColor(pct)} transition-all`} style={{ width: pct + "%" }} /></div>
        {sum && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Limit" value={inr(sum.max_limit)} testid="stat-limit" />
            <Stat label="Used (outstanding)" value={inr(sum.outstanding)} testid="stat-used" />
            <Stat label="Available" value={inr(sum.available)} testid="stat-available" />
            <Stat label="Pending approval" value={inr(sum.pending)} testid="stat-pending" />
          </div>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
        <h3 className="font-bold text-slate-900">Request settlement</h3>
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">Amount (₹)</Label><Input data-testid="settlement-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-white border-slate-300" /></div>
          <div><Label className="text-xs text-slate-500">Payment Mode <span className="text-rose-500">*</span></Label>
            <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger data-testid="settlement-mode-select" className="mt-1 bg-white border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(MODES).map(([k, m]) => <SelectItem key={k} value={k} data-testid={`settlement-mode-${k}`}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.payment_mode !== "cash" && (
            <div><Label className="text-xs text-slate-500">{mode.ref} <span className="text-rose-500">*</span></Label><Input data-testid={`settlement-${form.payment_mode}-ref-input`} value={form[mode.field]} onChange={(e) => setForm({ ...form, [mode.field]: e.target.value })} className="mt-1 bg-white border-slate-300 font-mono" /></div>
          )}
          <div className={form.payment_mode === "cash" ? "md:col-span-1" : "md:col-span-3"}><Label className="text-xs text-slate-500">Note {form.payment_mode === "cash" && <span className="text-rose-500">*</span>}</Label><Input data-testid="settlement-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={form.payment_mode === "cash" ? "e.g. handed to Ravi at office" : ""} className="mt-1 bg-white border-slate-300" /></div>
        </div>
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Screenshot / proof <span className="text-rose-500">*</span></Label>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="settlement-screenshot-input" />
          <button type="button" data-testid="settlement-screenshot-button" onClick={() => fileRef.current?.click()} className="mt-1 flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-4 py-2.5 hover:border-teal-400 transition-colors">
            {form.screenshot ? <img src={fileUrl(form.screenshot)} alt="proof" className="w-10 h-10 object-contain rounded bg-white p-0.5 border border-slate-200" /> : (uploading ? <Loader2 className="w-4 h-4 animate-spin text-teal-600" /> : <UploadCloud className="w-4 h-4 text-teal-600" />)}
            <span className="text-xs text-slate-500">{form.screenshot ? "Change screenshot" : "Upload screenshot"}</span>
          </button>
        </div>
        <Button data-testid="settlement-submit" onClick={submit} disabled={busy} className="mt-4 bg-teal-600 hover:bg-teal-700 text-white"><Plus className="w-4 h-4 mr-1" />{busy ? "Submitting..." : "Request Settlement"}</Button>
      </div>
      <SettlementHistory rows={rows} />
    </div>
  );
}

function SuperSettlement() {
  const [limits, setLimits] = useState([]);
  const [pending, setPending] = useState([]);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const load = () => {
    api.get("/super/branch-limits").then((r) => setLimits(r.data)).catch(() => {});
    api.get("/settlements", { params: { status: "Pending" } }).then((r) => setPending(r.data)).catch(() => {});
    api.get("/settlements").then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const decide = async (id, action, reason) => {
    try { await api.post(`/settlements/${id}/${action}`, action === "reject" ? { reason } : {}); toast.success(`Settlement ${action}d`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveLimit = async (b) => {
    const v = parseFloat(edit[b.branch_id]);
    if (isNaN(v) || v < 0) return toast.error("Invalid limit");
    try { await api.put(`/branches/${b.branch_id}/limit`, { max_limit: v }); toast.success("Limit updated"); setEdit({ ...edit, [b.branch_id]: undefined }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="settlement-panel-super">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 overflow-x-auto">
        <h2 className="text-lg font-bold text-slate-900">Pending settlement requests</h2>
        {pending.length === 0 ? <p className="text-sm text-slate-500 mt-2">No pending requests.</p> : (
          <table className="w-full mt-3 text-sm"><thead><tr className="border-b border-slate-200 text-left text-slate-500"><th className="py-2">Branch</th><th>Mode</th><th>Amount</th><th>Ref</th><th>Note</th><th>Proof</th><th>Actions</th></tr></thead>
          <tbody>{pending.map((s) => (
            <tr key={s.id} className="border-b border-slate-100" data-testid={`pending-settlement-${s.id}`}>
              <td className="py-2">{s.branch_name}</td><td data-testid={`pending-mode-${s.id}`}>{modeLabel(s)}</td><td>{inr(s.amount)}</td><td className="font-mono text-xs max-w-[200px] truncate">{s.usdt_tx_hash || s.bank_ref || "—"}</td><td>{s.note || "—"}</td><td><Proof s={s} /></td>
              <td className="flex gap-2 py-2">
                <Button size="sm" data-testid={`approve-settlement-${s.id}`} onClick={() => decide(s.id, "approve")} className="bg-teal-600 hover:bg-teal-700 text-white h-8"><Check className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" data-testid={`reject-settlement-${s.id}`} onClick={() => { const r = prompt("Reason for rejection?"); if (r !== null) decide(s.id, "reject", r); }} className="border-rose-200 text-rose-600 hover:bg-rose-50 h-8"><X className="w-3.5 h-3.5" /></Button>
              </td>
            </tr>))}</tbody></table>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 overflow-x-auto">
        <h2 className="text-lg font-bold text-slate-900">Branch limits & outstanding</h2>
        <table className="w-full mt-3 text-sm"><thead><tr className="border-b border-slate-200 text-left text-slate-500"><th className="py-2">Branch</th><th>Limit</th><th>Outstanding</th><th>Available</th><th>Pending</th><th className="w-40">Progress</th><th>Set limit</th></tr></thead>
        <tbody>{limits.map((b) => { const p = barPct(b.outstanding, b.max_limit); return (
          <tr key={b.branch_id} className="border-b border-slate-100" data-testid={`branch-limit-${b.branch_id}`}>
            <td className="py-2">{b.name}</td><td>{inr(b.max_limit)}</td><td>{inr(b.outstanding)}</td><td>{inr(b.available)}</td><td>{inr(b.pending)}</td>
            <td><div className="h-1.5 w-36 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${barColor(p)}`} style={{ width: p + "%" }} /></div></td>
            <td className="flex gap-2 py-2">
              <Input data-testid={`limit-input-${b.branch_id}`} value={edit[b.branch_id] ?? String(b.max_limit)} onChange={(e) => setEdit({ ...edit, [b.branch_id]: e.target.value })} className="h-8 w-28 bg-white border-slate-300" />
              <Button size="sm" data-testid={`save-limit-${b.branch_id}`} onClick={() => saveLimit(b)} className="bg-slate-800 hover:bg-slate-900 text-white h-8">Save</Button>
            </td>
          </tr>); })}</tbody></table>
      </div>
      <SettlementHistory rows={rows} showBranch />
    </div>
  );
}
